import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import HLS from 'hls.js'

import { MEDIA_KEY_SHORTCUTS } from 'vidstack'
import 'vidstack/player';
import 'vidstack/player/ui';
import 'vidstack/icons';

export default class extends Controller {
  static targets = [
    'annotationOverlay',
    'annotationTools',
    'annotationViewer',
    'conversationContainer',
    'conversationContainerForm',
    'conversationContainerInput',
    'mediaPlayer',
    'timelineComment',
  ]

  connect() {
    if (this.hasMediaPlayerTarget){
      this.setupPlayer()
    }

    window.addEventListener("resize", (e) => {
      this.modifyAnnotationSizes()
    })
  }

  async setupPlayer() {
    this.mediaPlayerTarget.keyShortcuts = {
      ...MEDIA_KEY_SHORTCUTS,
      seekBackward: ['ArrowLeft'],
      seekForward: ['ArrowRight'],
      slowDown: {
        keys: ['j', 'J', '<'],
        onKeyUp({ event, player, remote }) {
          const playbackRate = player.playbackRate
          const stepSize = playbackRate > 2 ? 1 : 0.25

          remote.changePlaybackRate(
            Math.max(stepSize, Math.min(8, playbackRate - stepSize)),
            event,
          )
        },
      },
      speedUp: {
        keys: ['l', 'L', '>'],
        onKeyUp({ event, player, remote }) {
          const playbackRate = player.playbackRate
          const stepSize = playbackRate >= 2 ? 1 : 0.25

          remote.changePlaybackRate(
            Math.max(stepSize, Math.min(8, playbackRate + stepSize)),
            event,
          )
        },
      },
    }

    this.mediaPlayerTarget.addEventListener('provider-change', (event) => {
      const provider = event.detail
      if (provider?.type === 'hls') {
        provider.library = HLS
      }
    })

    this.mediaPlayerTarget.addEventListener('can-play', (e) => {
      const { detail: { duration } } = e
      
      this.mediaPlayerTarget.style.setProperty('--media-duration', duration)
      
      this.setCommentPositions(duration)
    })

    this.mediaPlayerTarget.addEventListener('play', (e) => {
      this.clearAnnotations()
    })
  }

  setCommentPositions(mediaDuration){
    const commentOverlayWidth = 256

    if (this.hasTimelineCommentTarget){
      this.timelineCommentTargets.forEach((timelineComment) => {
        const timestampSeconds = Number(timelineComment.dataset.timestampSeconds)
        
        const {
          provider: {
            video: {
              clientWidth: width,
            },
          }
        } = this.mediaPlayerTarget

        const defaultPosition = (timestampSeconds / mediaDuration) * width
        
        if (defaultPosition > (width - commentOverlayWidth)){
          timelineComment.dataset.overflowsContainer = true
        }
      })
    }
  }

  selectComment(e){
    const { target: { dataset: { annotationData, timestampSeconds } } } = e

    // const { clipStartTime, clipEndTime, currentTime, duration } = this.mediaPlayerTarget.state

    this.toggleAnnotations(e, { isAnnotationCanvasVisible: true })

    this.setAnnotationData(annotationData)
    
    this.mediaPlayerTarget.currentTime = timestampSeconds
    
    this.pausePlayer()
  }
  
  setAnnotationData(annotationData){
    const annotationViewerController = this.getAnnotationViewerController()
    
    if (!annotationData){
      annotationViewerController.resetViewerState()
      return
    }

    annotationViewerController.setViewerState(JSON.parse(annotationData))
  }

  toggleAnnotations(e, options = {}){
    const annotationCanvasController = this.getAnnotationCanvasController()
    const isAnnotationCanvasVisible = options.isAnnotationCanvasVisible || annotationCanvasController.getVisibility()

    // Get Video Dimensions
    const { provider: { video } } = this.mediaPlayerTarget

    // Annotation Canvas
    this.toggleAnnotationCanvas({ isVisible: isAnnotationCanvasVisible, video })

    // Annotation Tools
    this.toggleAnnotationTools({ isVisible: isAnnotationCanvasVisible })

    // Annotation Viewer
    this.toggleAnnotationViewer({ isVisible: !isAnnotationCanvasVisible, video })

    // Disable Gestures while annotation overlay is open
    const mediaGestureElements = this.element.getElementsByTagName('media-gesture')

    for (let gestureElement of mediaGestureElements) {
      gestureElement.disabled = !isAnnotationCanvasVisible
    }
  }

  modifyAnnotationSizes(){
    const {
      provider: {
        video: {
          clientHeight: height,
          clientWidth: width,
        },
      }
    } = this.mediaPlayerTarget

    const annotationCanvasController = this.getAnnotationCanvasController()
    annotationCanvasController.modifyCanvasSize({
      height,
      width,
    })
    
    const annotationViewerController = this.getAnnotationViewerController()
    annotationViewerController.modifyViewerSize({
      height,
      width,
    })
  }

  toggleAnnotationCanvas(params){
    const { isVisible, video: { clientHeight, clientWidth } } = params

    const annotationCanvasController = this.getAnnotationCanvasController()
    annotationCanvasController.toggleVisibility({
      isVisible,
      height: clientHeight,
      width: clientWidth,
    })
  }

  toggleAnnotationTools(params){
    const { isVisible } = params

    const annotationToolsController = this.getAnnotationToolsController()
    annotationToolsController.toggleVisibility({
      isVisible,
    })
  }

  toggleAnnotationViewer(params){
    const { isVisible, video: { clientHeight, clientWidth } } = params

    const annotationViewerController = this.getAnnotationViewerController()
    annotationViewerController.toggleVisibility({
      isVisible,
      height: clientHeight,
      width: clientWidth,
    })
  }

  clearAnnotations(){
    const { provider: { video } } = this.mediaPlayerTarget

    this.toggleAnnotationCanvas({ isVisible: true, video })
    this.toggleAnnotationTools({ isVisible: true })
    this.toggleAnnotationViewer({ isVisible: true, video })
  }

  pausePlayer(){
    this.mediaPlayerTarget.pause()
  }

  getAnnotationCanvasController(){
    return this.application.getControllerForElementAndIdentifier(this.annotationOverlayTarget, 'video-reviewer--player--annotation-canvas')
  }

  getAnnotationViewerController(){
    return this.application.getControllerForElementAndIdentifier(this.annotationViewerTarget, 'video-reviewer--player--annotation-viewer')
  }
  
  getAnnotationToolsController(){
    return this.application.getControllerForElementAndIdentifier(this.annotationToolsTarget, 'video-reviewer--annotation-tools')
  }

  selectMarkerName(e){
    const { target: { dataset: { markerName } } } = e

    const annotationCanvasController = this.getAnnotationCanvasController()
    annotationCanvasController.selectMarkerName(markerName)
    
    const annotationToolsController = this.getAnnotationToolsController()
    annotationToolsController.selectMarkerName(markerName)
  }

  selectMarkerProperty(e){
    const { target: { dataset: { markerPropertyName, markerPropertyValue } } } = e

    const annotationCanvasController = this.getAnnotationCanvasController()
    annotationCanvasController.selectMarkerProperty(markerPropertyName, markerPropertyValue)
    
    const annotationToolsController = this.getAnnotationToolsController()
    annotationToolsController.selectMarkerProperty(markerPropertyName, markerPropertyValue)
  }

  async setCommentAttributes(e){
    e.preventDefault()

    const formData = new FormData(this.conversationContainerFormTarget)
    formData.append("comment[timestamp_seconds]", this.mediaPlayerTarget.currentTime)

    let annotationData
    if (this.annotationOverlayTarget.dataset.visible === 'true'){
      const annotationCanvasController = this.getAnnotationCanvasController()
      annotationData = await annotationCanvasController.getEditorState()
      formData.append("comment[annotation_data]", JSON.stringify(annotationData))
    }

    try {
      const request = await post(this.conversationContainerFormTarget.action, {
        body: formData,
        query: {
          video_reviewer: true,
        },
        responseKind: 'turbo-stream',
      })

      if (request.ok){
        this.conversationContainerInputTarget.value = ''

        const event = new Event("comment:created")
        document.dispatchEvent(event)
      }
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }
  
}
